import { useMemo, useRef, useEffect } from "preact/hooks";
import { DailyProvider } from "@daily-co/daily-react";
import DailyIframe from "@daily-co/daily-js";
import { Style } from "./styles/Style";
import VideoChatWidget from "./VideoChatWidget";
import { MessageProvider } from "./providers/MessageProvider";
import { MuteProvider } from "./providers/MuteProvider";
import { AgentProvider } from "./providers/AgentProvider";
import { WidgetProvider } from "./providers/WidgetProvider";
import { UserInteractionProvider } from "./providers/UserInteractionProvider";
import { ErrorProvider } from "./providers/ErrorProvider";

interface LemonSliceWidgetProps {
  "agent-id"?: string;
  "inline"?: string | boolean;
  "show-minimize-button"?: string | boolean;
}

// Check if device is mobile
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export default function LemonSliceWidget(props: LemonSliceWidgetProps = {}) {
  const agentId = props["agent-id"] || import.meta.env.VITE_AGENT_ID;
  const inlineProp = props["inline"];
  // Handle: undefined (not set) = false (floating), "true" or bare attribute = true (inline), "false" = false (floating)
  const isInline = inlineProp === "true" || inlineProp === "" || inlineProp === true;
  const showMinimizeButtonProp = props["show-minimize-button"];
  // Handle: undefined (not set) = true (show button), "true" or bare attribute = true (show), "false" = false (hide)
  const showMinimizeButton = showMinimizeButtonProp === undefined || showMinimizeButtonProp === "true" || showMinimizeButtonProp === "" || showMinimizeButtonProp === true;

  // Hide widget on mobile devices
  if (isMobileDevice()) {
    return null;
  }

  const callObject = useMemo(() => {
    return DailyIframe.createCallObject({
      videoSource: false,
      audioSource: true,
    });
  }, []);

  // Cleanup: destroy call object on unmount
  useEffect(() => {
    return () => {
      if (callObject) {
        try {
          // Leave any active calls first
          const meetingState = callObject.meetingState();
          if (meetingState === "joined-meeting" || meetingState === "joining-meeting") {
            callObject.leave();
          }
        } catch (error) {
          // Ignore errors if not in a meeting
        }

        // Destroy the call object to prevent duplicate instances
        try {
          callObject.destroy();
        } catch (error) {
          console.error("Error destroying call object:", error);
        }
      }
    };
  }, [callObject]);

  const setShowMinimizeButtonRef = useRef<((show: boolean) => void) | null>(null);

  // Expose method on the custom element
  useEffect(() => {
    // Get the custom element (host element)
    const customElement = document.querySelector('lemon-slice-widget');
    if (customElement) {
      (customElement as any).setShowMinimizeButton = (show: boolean) => {
        setShowMinimizeButtonRef.current?.(show);
      };
    }
  }, []);

  return (
    <div translate={false}>
      <AgentProvider>
        <UserInteractionProvider>
          <DailyProvider callObject={callObject}>
            <WidgetProvider showMinimizeButton={showMinimizeButton} onSetShowMinimizeButton={(setter) => { setShowMinimizeButtonRef.current = setter; }}>
              <ErrorProvider>
                <MuteProvider>
                  <MessageProvider>
                    <Style />
                    <VideoChatWidget agentId={agentId} isInline={isInline} />
                  </MessageProvider>
                </MuteProvider>
              </ErrorProvider>
            </WidgetProvider>
          </DailyProvider>
        </UserInteractionProvider>
      </AgentProvider>
    </div>
  );
}
